'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from './heading';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { Carousel } from './carousel';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { CarouselProgress } from './carousel-progress';
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';

interface ProductsCarouselProps {
  title: string;
  hideIcon?: boolean;
  products: Product[];
  locale?: string;
  query?: FetchProductQuery;
  className?: {
    section?: string;
    container?: string;
  };
}

export function ProductsCarousel({
  title,
  hideIcon = false,
  products: initialProducts,
  locale,
  query,
  className,
}: ProductsCarouselProps) {
  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const personalizedProductsQuery = useQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      'homepage-products-carousel',
      locale,
      query,
      me?.customer.id ?? 'guest',
    ],
    queryFn: ({ signal }) =>
      fetchProducts({ locale: locale!, query, authToken, signal }).then(response => response.data),
    enabled: isInitialized && !!authToken && !!locale && !!query,
    placeholderData: initialProducts,
    staleTime: 0,
  });
  const products = personalizedProductsQuery.data ?? initialProducts;

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'relative gap-6 overflow-visible pr-0 sm:gap-8 sm:pr-4 lg:gap-12',
        className?.section,
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex items-center justify-between overflow-visible pr-4 sm:pr-0',
          className?.container,
        )}
      >
        <div className="flex items-center justify-start gap-4">
          {!hideIcon && (
            <Image
              src={'/images/like.svg'}
              alt="Like"
              width={48}
              height={48}
              className="shrink-0"
            />
          )}
          <Heading title={title} tag="h2" className={{ title: 'uppercase' }} />
        </div>
        <CarouselControls
          className={{
            main: 'hidden sm:flex',
            button:
              'max-h-12 min-h-12 min-w-12 max-w-12 rounded-[4px] bg-black text-white hover:bg-black/75',
          }}
          nextIcon={<MdOutlineKeyboardArrowRight size={24} />}
          prevIcon={<MdOutlineKeyboardArrowLeft size={24} />}
        />
      </div>
      <div className={cn('custom-container', 'overflow-visible')}>
        <Carousel
          options={options}
          className={{
            wrapper: 'w-full',
            main: 'w-full',
            container: 'w-full gap-4 sm:gap-5 lg:gap-6',
            slide: 'w-full max-w-[282px]',
          }}
          slides={products.map(product => (
            <ProductCard product={product} key={product.slug} />
          ))}
        />
      </div>
      <div className="flex w-full pr-4 sm:hidden">
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={products.length} />
      </div>
    </section>
  );
}
